import { showNotification } from '@mantine/notifications';
import { Button, Icon } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { Monitor } from '../../../api';
import {
	monitorMetricsQueryKeyFactory,
	queryClient,
	useRunMonitors,
} from '../../../api';
import { monitorsQueryKeyFactory } from '../../../api/hooks/monitoring/constants';
import type { IBackgroundJob } from '../../../api/types/models/backgroundJob';
import { BackgroundJobProgress } from '../../BackgroundJobProgress/BackgroundJobProgress';

export interface IRunMonitorsProps {
	size?: 'sm' | 'md' | 'lg';
	monitor: Monitor;
	onSuccess?: VoidFunction;
	disabled?: boolean;
	variant?: 'default' | 'primary';
}

function RunMonitors({
	size = 'md',
	monitor,
	onSuccess,
	disabled = false,
	variant = 'default',
}: IRunMonitorsProps) {
	const { backgroundJob, resetBackgroundJob, runMonitors } = useRunMonitors();

	const handleRunMonitors = async () => {
		await runMonitors([monitor.id]);
	};

	const handleBackgroundJobComplete = (job?: IBackgroundJob) => {
		resetBackgroundJob();
		if (onSuccess) {
			onSuccess();
		}

		if (job?.failed) {
			showNotification({
				title: 'Monitors failed to run',
				message: 'Monitors encountered errors when running',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} else {
			queryClient.invalidateQueries({
				queryKey: monitorsQueryKeyFactory.list(),
			});
			queryClient.invalidateQueries({
				queryKey: monitorMetricsQueryKeyFactory.all(),
			});
			showNotification({
				title: 'Monitors ran successfully',
				message: 'All the selected monitors ran successfully',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
		}
	};

	return (
		<>
			{backgroundJob && (
				<BackgroundJobProgress
					key={backgroundJob.id}
					job={backgroundJob}
					onCompleted={handleBackgroundJobComplete}
				/>
			)}
			<Button
				disabled={disabled}
				size={size}
				variant={variant}
				onClick={handleRunMonitors}
			>
				Run monitor
			</Button>
		</>
	);
}

const RunMonitorsObserver = observer(RunMonitors);
export default RunMonitorsObserver;
